/**
 * threshold-events.ts — WEB.4.UI.14
 *
 * Pollution-threshold consequence cards (per docx "Post-Event Consequence
 * Cards"). At end of each era (era change), evaluate accumulated global
 * pollution and fire ONE matching threshold card:
 *
 *   ≥ 50 pollution → Critical pool (most punishing)
 *   ≥ 35 pollution → High pool
 *   ≥ 20 pollution → Medium pool
 *   < 20         → no event
 *
 * Each card broadcasts to ALL active (non-bankrupt) seats. This is the
 * difficulty curve — without it, players can hoard fossil-fueled tokens
 * with no in-game pressure until the 200-collapse ceiling.
 *
 * v1 scope: 5 cards wired with TokenDelta/EnergyDelta (simple resource
 * penalties). 10 more cards (Discard hand / Destroy random facility /
 * Give facility) are listed but marked deferred — they need new effect
 * kinds out of scope for this sub-phase.
 */

export type ThresholdTier = "medium" | "high" | "critical";

export interface ThresholdCard {
  id: string;
  cardName: string;
  description: string;
  tier: ThresholdTier;
  /** Per-seat resource deltas applied to ALL active seats. */
  tokenDelta?: number;
  energyDelta?: number;
  pollutionDelta?: number;
  /**
   * When true, this card has effects beyond simple resource deltas that
   * UI.14 doesn't yet wire (e.g. discard random card, destroy random facility).
   * Auto-trigger still picks these cards but logs a TODO warning and skips
   * the unsupported part.
   */
  deferred?: boolean;
  educationalFact: string;
}

export const THRESHOLD_CARDS: ReadonlyArray<ThresholdCard> = [
  // ─── Medium (20+) ────────────────────────────────────────────────────────
  {
    id: "Health_Warning_Issued",
    cardName: "Health Warning Issued",
    description: "Air quality reaches hazardous levels.",
    tier: "medium",
    tokenDelta: -15,
    educationalFact:
      "WHO 2019 estimates 4.2 million annual deaths globally from ambient (outdoor) air pollution. Source: WHO Air Pollution Fact Sheet (2021).",
  },
  {
    id: "Investor_Doubt_Grows",
    cardName: "Investor Doubt Grows",
    description: "Uncertainty over energy policy deters investors.",
    tier: "medium",
    deferred: true,
    educationalFact:
      "IEA 2024: global clean-energy investment hit USD 2 trillion in 2024 but remains hostage to policy uncertainty in major economies. Source: IEA World Energy Investment (2024).",
  },
  {
    id: "Cross_Border_Tensions",
    cardName: "Cross-Border Tensions",
    description: "Air or water pollution affects neighboring regions.",
    tier: "medium",
    tokenDelta: -10,
    educationalFact:
      "The 2002–03 Southeast Asian haze, traced to Indonesian peat fires, caused USD 9 billion in regional economic damage. Source: ASEAN Haze Action Plan retrospective.",
  },
  {
    id: "Insurance_Premiums_Rise",
    cardName: "Insurance Premiums Rise",
    description: "Energy infrastructure now considered high-risk.",
    tier: "medium",
    tokenDelta: -15,
    educationalFact:
      "Lloyd's of London reported a 7.5% YoY premium increase for energy-sector property in 2023, citing climate-related catastrophe exposure. Source: Lloyd's Market Bulletin Y5388.",
  },
  {
    id: "NGO_Surveillance",
    cardName: "NGO Surveillance",
    description: "Climate groups begin auditing your strategy.",
    tier: "medium",
    energyDelta: -3,
    educationalFact:
      "Climate-watchdog NGOs publish annual emissions reports of major corporations — Climate Action 100+ tracks 167 systemically important emitters covering 80% of global industrial emissions. Source: Climate Action 100+ Net Zero Benchmark.",
  },

  // ─── High (35+) ──────────────────────────────────────────────────────────
  {
    id: "Global_Climate_Marches",
    cardName: "Global Climate Marches",
    description: "Mass protests erupt demanding change.",
    tier: "high",
    deferred: true,
    educationalFact:
      "The September 2019 global climate strike mobilised an estimated 6 million people in over 4,500 events across 150 countries. Source: 350.org event tally (2019).",
  },
  {
    id: "UN_Sanctions_On_Polluters",
    cardName: "UN Sanctions on Polluters",
    description: "International community steps in.",
    tier: "high",
    deferred: true,
    educationalFact:
      "The Paris Agreement (2015) ratification by 195 parties created a non-binding compliance framework. Hard sanctions remain proposed but unenacted. Source: UNFCCC Paris Agreement Text.",
  },
  {
    id: "Climate_Migration_Wave",
    cardName: "Climate Migration Wave",
    description: "Infrastructure pressure due to displaced communities.",
    tier: "high",
    tokenDelta: -30,
    educationalFact:
      "World Bank Groundswell Report (2021): up to 216 million people could be internally displaced by climate impacts across 6 regions by 2050. Source: World Bank Groundswell (2021).",
  },
  {
    id: "Energy_Credit_Downgrade",
    cardName: "Energy Credit Downgrade",
    description: "Markets lose faith in national energy stability.",
    tier: "high",
    tokenDelta: -10,
    energyDelta: -6,
    educationalFact:
      "Moody's downgraded the credit ratings of three coal-heavy US utilities in 2020 citing 'energy transition risk' for the first time. Source: Moody's Investor Service press release Aug 2020.",
  },
  {
    id: "Climate_Emergency_Declaration",
    cardName: "Climate Emergency Declaration",
    description: "Government forced into reactive measures.",
    tier: "high",
    deferred: true,
    educationalFact:
      "As of 2023, over 2,300 jurisdictions in 40 countries — covering 1+ billion people — had declared a climate emergency. Source: Climate Emergency Declaration registry (2023).",
  },

  // ─── Critical (50+) ──────────────────────────────────────────────────────
  {
    id: "Climate_Collapse",
    cardName: "Climate Collapse",
    description: "Natural systems break down — extreme, irreversible damage.",
    tier: "critical",
    deferred: true,
    educationalFact:
      "IPCC AR6 WGII (2022) identifies multiple climate tipping points — Amazon dieback, West Antarctic ice sheet collapse, Greenland ice loss — that could cascade beyond +1.5°C. Source: IPCC AR6 WGII SPM.B.5.",
  },
  {
    id: "Mass_Extinction_Event",
    cardName: "Mass Extinction Event Begins",
    description: "Biodiversity loss triggers ecological shock.",
    tier: "critical",
    tokenDelta: -30,
    energyDelta: -10,
    educationalFact:
      "IPBES 2019 Global Assessment: ~1 million species threatened with extinction within decades; current extinction rate is tens to hundreds of times higher than the past 10 million years' baseline. Source: IPBES Global Assessment (2019).",
  },
  {
    id: "UN_Sanctions_Severe",
    cardName: "UN Sanctions on Polluters (severe)",
    description: "International community steps in — severe.",
    tier: "critical",
    deferred: true,
    educationalFact:
      "Hypothetical UN Security Council climate-emergency sanctions remain politically blocked by major fossil-fuel exporters. Source: SIPRI Climate, Peace & Security Index (2023).",
  },
  {
    id: "Global_Energy_Forum_Crisis_Summit",
    cardName: "Global Energy Forum Crisis Summit",
    description: "Emergency talks force all countries to act.",
    tier: "critical",
    deferred: true,
    educationalFact:
      "COP28 (2023) was the first UNFCCC conference to formally call for 'transitioning away from fossil fuels in energy systems' — though no binding shutdown timeline was set. Source: UNFCCC COP28 Decision 1/CMA.5.",
  },
  {
    id: "Tipping_Point_Passed",
    cardName: "Tipping Point Passed",
    description: "Planetary feedback loops engage.",
    tier: "critical",
    pollutionDelta: 20,
    tokenDelta: -20,
    energyDelta: -5,
    educationalFact:
      "IPCC AR6 'tipping element' analysis: ~16 abrupt-change risks cluster between +1.5°C and +2°C global warming. Pollution feedbacks (permafrost methane, peatland fire) self-amplify once crossed. Source: IPCC AR6 WGI Ch.5.",
  },
];

const TIER_FOR_POLLUTION_BY_PRIORITY: ReadonlyArray<{
  min: number;
  tier: ThresholdTier;
}> = [
  { min: 50, tier: "critical" },
  { min: 35, tier: "high" },
  { min: 20, tier: "medium" },
];

/**
 * Returns the threshold tier matched by current global pollution, or null
 * when below all thresholds.
 */
export function tierForPollution(globalPollution: number): ThresholdTier | null {
  for (const t of TIER_FOR_POLLUTION_BY_PRIORITY) {
    if (globalPollution >= t.min) return t.tier;
  }
  return null;
}

/**
 * Pick a random card from the tier pool using the supplied rng.
 */
export function pickThresholdCard(
  tier: ThresholdTier,
  rng: { nextInt: (min: number, max: number) => number },
): ThresholdCard | null {
  const pool = THRESHOLD_CARDS.filter((c) => c.tier === tier);
  if (pool.length === 0) return null;
  const idx = rng.nextInt(0, pool.length - 1);
  return pool[idx] ?? null;
}

/**
 * Rank a tier for upward-crossing comparison (null < medium < high < critical).
 * Codex UI.14 R1 HIGH 1 — fire only when current > prior tier rank.
 */
export function thresholdTierRank(tier: ThresholdTier | null): number {
  if (tier === null) return 0;
  if (tier === "medium") return 1;
  if (tier === "high") return 2;
  return 3; // critical
}
